/**
 * The single fetch wrapper every generated API call goes through (orval mutator).
 * Same-origin session cookies, CSRF header from the XSRF-TOKEN cookie on mutating
 * requests (plan.md D-6), JSON in and out, typed errors.
 */

export class ApiError extends Error {
  readonly status: number
  readonly body: unknown

  constructor(status: number, body: unknown) {
    super(`API request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`
  const part = document.cookie.split('; ').find((c) => c.startsWith(prefix))
  return part ? decodeURIComponent(part.slice(prefix.length)) : undefined
}

async function parseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined
  const text = await response.text()
  if (text.length === 0) return undefined
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

export async function http<T>(url: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase()
  const headers = new Headers(init.headers)
  headers.set('Accept', 'application/json')
  // Only JSON bodies get a content type here; the browser sets the multipart boundary for FormData.
  if (typeof init.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  if (!SAFE_METHODS.has(method)) {
    const token = readCookie('XSRF-TOKEN')
    if (token) headers.set('X-XSRF-TOKEN', token)
  }

  const response = await fetch(url, { ...init, method, headers, credentials: 'same-origin' })
  const body = await parseBody(response)
  if (!response.ok) {
    throw new ApiError(response.status, body)
  }
  return body as T
}

export default http
